/**
 * SocialPublishModal — compositor de publicación desde un asset.
 *
 * No publica nada al abrirse: valida, muestra un resumen y exige una
 * confirmación final. No consume créditos de generación.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, ArrowLeft, CalendarClock, CheckCircle2, Clock,
  Loader2, Send, X, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { socialService } from '../../services/socialService';
import {
  PlatformIcon, platformMeta, TARGET_STATUS_LABEL,
} from './platforms';

const newKey = () => (window.crypto?.randomUUID?.() || `sml-${Date.now()}-${Math.random()}`);
const POST_TYPE_LABEL = { post: 'Publicación', reel: 'Reel', story: 'Historia' };
// Envío AP-1 · YouTube por Buffer = Shorts (título y categoría obligatorios).
const YT_CATEGORIES = [
  ['22', 'Personas y blogs'], ['27', 'Educación'], ['28', 'Ciencia y tecnología'],
  ['24', 'Entretenimiento'], ['1', 'Cine y animación'], ['10', 'Música'],
  ['26', 'Guías y estilo'], ['19', 'Viajes y eventos'], ['17', 'Deportes'],
  ['23', 'Comedia'], ['20', 'Videojuegos'], ['15', 'Mascotas y animales'],
  ['2', 'Autos y vehículos'], ['25', 'Noticias y política'], ['29', 'ONG y activismo'],
];
const STORY_MAX_SECONDS = 60;
const SHORT_MAX_SECONDS = 180;
const firstLine = (text) => (text || '').split('\n').map((l) => l.trim()).find(Boolean) || '';
const fmtSecs = (s) => {
  const t = Math.round(s || 0);
  return t < 60 ? `${t} s` : `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')} min`;
};
const localTz = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'UTC'; }
};

const defaultDate = () => {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  return { date: d.toISOString().slice(0, 10), time: d.toTimeString().slice(0, 5) };
};

export const SocialPublishModal = ({ asset, onClose }) => {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [selected, setSelected] = useState([]);
  const [caption, setCaption] = useState('');
  const [perChannel, setPerChannel] = useState({});
  const [perOptions, setPerOptions] = useState({});
  const [editing, setEditing] = useState(null);
  const [mode, setMode] = useState('now');
  const [when, setWhen] = useState(defaultDate);
  const [step, setStep] = useState('compose');
  const [report, setReport] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(newKey);
  const [mediaInfo, setMediaInfo] = useState(null);   // { duration, width, height } del video
  const [ytOptions, setYtOptions] = useState({});     // { [channelId]: { title, category_id, ai_generated } }

  const tz = useMemo(localTz, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await socialService.status();
        if (!alive) return;
        setConnected(!!data.connected);
        setChannels((data.channels || []).filter((c) => c.status !== 'removed'));
      } catch (err) {
        if (alive) toast.error(err?.response?.data?.detail || 'No pudimos leer tus redes.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const scheduledAt = useMemo(() => {
    if (mode !== 'scheduled') return null;
    const value = new Date(`${when.date}T${when.time}`);
    return Number.isNaN(value.getTime()) ? null : value.toISOString();
  }, [mode, when]);

  const payload = () => ({
    asset_id: asset.id,
    channel_ids: selected,
    caption,
    per_channel_captions: Object.fromEntries(
      Object.entries(perChannel).filter(([id, text]) => selected.includes(id) && text?.trim())),
    per_channel_options: Object.fromEntries(selected.map((id) => {
      const channel = channels.find((c) => c.id === id);
      const options = {};
      if ((channel?.capabilities?.post_types || []).length > 0) options.post_type = perOptions[id] || 'post';
      if (mediaInfo) options.media_info = mediaInfo;
      if (channel?.platform === 'youtube') {
        const yt = ytOptions[id] || {};
        options.title = (yt.title ?? '').trim() || firstLine(perChannel[id] || caption).slice(0, 100);
        options.category_id = yt.category_id || '22';
        options.ai_generated = yt.ai_generated ?? true;
      }
      return [id, options];
    })),
    scheduled_at: scheduledAt,
    timezone: tz,
  });

  const toggleChannel = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    setReport(null);
  };

  const review = async () => {
    if (selected.length === 0) {
      toast.error('Elegí al menos una red.');
      return;
    }
    setBusy(true);
    try {
      const data = await socialService.validate(payload());
      setReport(data);
      if (data.ok) setStep('confirm');
      else toast.error(data.errors?.[0]?.message || 'Revisá los datos de la publicación.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No pudimos validar la publicación.');
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const data = await socialService.publish({
        ...payload(),
        publish_mode: mode === 'scheduled' ? 'scheduled' : 'now',
        idempotency_key: idempotencyKey,
      });
      setResult(data);
      setStep('result');
      const failed = (data.targets || []).filter((t) => t.status === 'failed').length;
      if (failed === 0) {
        toast.success(mode === 'scheduled' ? 'Publicación programada' : 'Publicación enviada');
      } else if (failed === (data.targets || []).length) {
        toast.error('Ninguna red aceptó la publicación');
      } else {
        toast.warning('Publicación parcial: revisá el detalle por red');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No pudimos publicar.');
    } finally { setBusy(false); }
  };

  const restart = () => {
    setIdempotencyKey(newKey());
    setResult(null);
    setReport(null);
    setStep('compose');
  };

  const selectedChannels = channels.filter((c) => selected.includes(c.id));

  return (
    <div className="social-modal-overlay" onClick={onClose} data-testid="social-publish-modal">
      <div className="social-modal" onClick={(e) => e.stopPropagation()}>
        <header className="social-modal-head">
          <div>
            <h2>Publicar en redes</h2>
            <p>
              {step === 'compose' && 'Elegí las redes, escribí el texto y revisá antes de confirmar.'}
              {step === 'confirm' && 'Último paso: confirmá y publicamos.'}
              {step === 'result' && 'Resultado por red.'}
            </p>
          </div>
          <button className="social-icon-btn" onClick={onClose} data-testid="social-publish-close">
            <X size={18} />
          </button>
        </header>

        <div className="social-modal-body">
          <aside className="social-preview">
            {asset.asset_type === 'video' ? (
              <video src={asset.url} controls preload="metadata" data-testid="social-preview-video"
                     onLoadedMetadata={(e) => {
                       const v = e.currentTarget;
                       if (v.duration && Number.isFinite(v.duration)) {
                         setMediaInfo({ duration: Math.round(v.duration * 10) / 10,
                                        width: v.videoWidth || undefined, height: v.videoHeight || undefined });
                       }
                     }} />
            ) : (
              <img src={asset.url} alt="Vista previa" data-testid="social-preview-image" />
            )}
            <span className="social-preview-meta">
              {asset.asset_type === 'video' ? 'Video' : 'Imagen'}
              {mediaInfo ? ` · ${fmtSecs(mediaInfo.duration)}` : ''}
              {mediaInfo?.width && mediaInfo?.height ? ` · ${mediaInfo.width}×${mediaInfo.height}` : ''}
              {!mediaInfo && asset.mime_type ? ` · ${asset.mime_type}` : ''}
            </span>
          </aside>

          <section className="social-compose">
            {loading ? (
              <div className="social-loading"><Loader2 size={16} className="animate-spin" /> Cargando redes…</div>
            ) : !connected ? (
              <div className="social-alert warning" data-testid="social-publish-not-connected">
                <AlertTriangle size={15} />
                <span>
                  Todavía no conectaste tus redes. Andá a Configuración → Redes sociales
                  y conectá tu cuenta de Buffer.
                </span>
              </div>
            ) : step === 'result' ? (
              <ResultList result={result} onRestart={restart} />
            ) : step === 'confirm' ? (
              <Summary
                channels={selectedChannels}
                caption={caption}
                perChannel={perChannel}
                perOptions={perOptions}
                mode={mode}
                when={when}
                tz={tz}
                warnings={report?.warnings || []}
              />
            ) : (
              <>
                <div className="social-field">
                  <label>Redes</label>
                  <div className="social-channel-picker" data-testid="social-channel-picker">
                    {channels.length === 0 && (
                      <span className="social-note small">
                        No hay canales sincronizados. Sincronizalos en Configuración → Redes sociales.
                      </span>
                    )}
                    {channels.map((channel) => {
                      const disabled = channel.status !== 'connected';
                      const active = selected.includes(channel.id);
                      return (
                        <button
                          key={channel.id}
                          className={`social-channel-pill ${active ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
                          onClick={() => !disabled && toggleChannel(channel.id)}
                          title={disabled ? 'Este canal necesita atención en Buffer' : ''}
                          data-testid={`social-pick-channel-${channel.id}`}
                        >
                          <PlatformIcon platform={channel.platform} size={13} />
                          {channel.display_name || platformMeta(channel.platform).label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="social-field">
                  <label>
                    Texto general
                    <span className="social-counter">{caption.length}</span>
                  </label>
                  <textarea
                    value={caption}
                    onChange={(e) => { setCaption(e.target.value); setReport(null); }}
                    rows={4}
                    placeholder="Escribí el texto que acompaña la publicación…"
                    data-testid="social-caption-input"
                  />
                </div>

                {selectedChannels.length > 0 && (
                  <div className="social-field">
                    <label>Texto por red (opcional)</label>
                    <ul className="social-percaption-list">
                      {selectedChannels.map((channel) => {
                        const open = editing === channel.id;
                        const value = perChannel[channel.id] ?? '';
                        const limit = channel.capabilities?.text_limit;
                        return (
                          <li key={channel.id}>
                            <button
                              className="social-percaption-toggle"
                              onClick={() => setEditing(open ? null : channel.id)}
                              data-testid={`social-edit-caption-${channel.id}`}
                            >
                              <PlatformIcon platform={channel.platform} size={12} />
                              {channel.display_name || platformMeta(channel.platform).label}
                              <span>{value ? 'personalizado' : 'usa el general'}</span>
                            </button>
                            {open && (
                              <textarea
                                rows={3}
                                value={value}
                                placeholder={caption || 'Texto para esta red…'}
                                onChange={(e) => {
                                  setPerChannel((prev) => ({ ...prev, [channel.id]: e.target.value }));
                                  setReport(null);
                                }}
                                data-testid={`social-caption-${channel.id}`}
                              />
                            )}
                            {open && limit ? (
                              <span className="social-counter inline">
                                {(value || caption || '').length} / {limit}
                              </span>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {selectedChannels.some((c) => (c.capabilities?.post_types || []).length > 0) && (
                  <div className="social-field">
                    <label>Formato por red</label>
                    <ul className="social-posttype-list">
                      {selectedChannels
                        .filter((c) => (c.capabilities?.post_types || []).length > 0)
                        .map((channel) => (
                          <li key={channel.id}>
                            <span>
                              <PlatformIcon platform={channel.platform} size={12} />
                              {channel.display_name || platformMeta(channel.platform).label}
                            </span>
                            <div className="social-posttype-options">
                              {channel.capabilities.post_types.map((type) => {
                                const tooLong = type === 'story' && asset.asset_type === 'video'
                                  && mediaInfo?.duration > STORY_MAX_SECONDS;
                                return (
                                  <button
                                    key={type}
                                    className={(perOptions[channel.id] || 'post') === type ? 'active' : ''}
                                    disabled={tooLong}
                                    title={tooLong ? `Las historias admiten videos de hasta ${STORY_MAX_SECONDS} s` : undefined}
                                    onClick={() => {
                                      setPerOptions((prev) => ({ ...prev, [channel.id]: type }));
                                      setReport(null);
                                    }}
                                    data-testid={`social-posttype-${channel.id}-${type}`}
                                  >
                                    {POST_TYPE_LABEL[type] || type}
                                  </button>
                                );
                              })}
                            </div>
                            {asset.asset_type === 'video' && mediaInfo?.duration > STORY_MAX_SECONDS && (
                              <span className="social-note small" data-testid={`social-story-hint-${channel.id}`}>
                                Historia: hasta {STORY_MAX_SECONDS} s y este video dura {fmtSecs(mediaInfo.duration)}.
                                {channel.platform === 'instagram' && ' Como Publicación o Reel sale como Reel.'}
                              </span>
                            )}
                          </li>
                        ))}
                    </ul>
                  </div>
                )}

                {selectedChannels.filter((c) => c.platform === 'youtube').map((channel) => {
                  const yt = ytOptions[channel.id] || {};
                  const setYt = (patch) => {
                    setYtOptions((prev) => ({ ...prev, [channel.id]: { ...(prev[channel.id] || {}), ...patch } }));
                    setReport(null);
                  };
                  const horizontal = mediaInfo?.width && mediaInfo?.height && mediaInfo.width > mediaInfo.height * 1.05;
                  const tooLong = mediaInfo?.duration > SHORT_MAX_SECONDS;
                  return (
                    <div className="social-field" key={`yt-${channel.id}`} data-testid={`social-youtube-${channel.id}`}>
                      <label>
                        <span><PlatformIcon platform="youtube" size={12} /> YouTube Short · {channel.display_name || 'YouTube'}</span>
                        <span className="social-counter">{(yt.title ?? '').length} / 100</span>
                      </label>
                      <input
                        className="social-input"
                        value={yt.title ?? ''}
                        maxLength={100}
                        placeholder={firstLine(perChannel[channel.id] || caption) || 'Título del video'}
                        onChange={(e) => setYt({ title: e.target.value })}
                        data-testid={`social-youtube-title-${channel.id}`}
                      />
                      <div className="social-schedule-row">
                        <select
                          value={yt.category_id || '22'}
                          onChange={(e) => setYt({ category_id: e.target.value })}
                          aria-label="Categoría de YouTube"
                          data-testid={`social-youtube-category-${channel.id}`}
                        >
                          {YT_CATEGORIES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                        </select>
                        <label className="social-inline-check">
                          <input type="checkbox" checked={yt.ai_generated ?? true}
                                 onChange={(e) => setYt({ ai_generated: e.target.checked })} />
                          Contenido generado con IA
                        </label>
                      </div>
                      <span className="social-note small">
                        Por Buffer, YouTube publica sólo Shorts: video vertical (9:16) o cuadrado, de hasta 3 minutos.
                        Si dejás el título vacío usamos la primera línea del texto.
                      </span>
                      {(horizontal || tooLong) && (
                        <div className="social-alert warning" data-testid={`social-youtube-warning-${channel.id}`}>
                          <AlertTriangle size={15} />
                          <span>
                            {horizontal && 'Este video es horizontal: YouTube no lo va a aceptar como Short. Exportá una versión 9:16 para publicarlo acá. '}
                            {tooLong && `Dura ${fmtSecs(mediaInfo.duration)}: los Shorts van hasta 3 minutos.`}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}

                <div className="social-field">
                  <label>Cuándo</label>
                  <div className="social-mode-switch">
                    <button
                      className={mode === 'now' ? 'active' : ''}
                      onClick={() => { setMode('now'); setReport(null); }}
                      data-testid="social-mode-now"
                    >
                      <Send size={13} /> Publicar ahora
                    </button>
                    <button
                      className={mode === 'scheduled' ? 'active' : ''}
                      onClick={() => { setMode('scheduled'); setReport(null); }}
                      data-testid="social-mode-schedule"
                    >
                      <CalendarClock size={13} /> Programar
                    </button>
                  </div>
                  {mode === 'scheduled' && (
                    <div className="social-schedule-row">
                      <input
                        type="date"
                        value={when.date}
                        onChange={(e) => { setWhen((w) => ({ ...w, date: e.target.value })); setReport(null); }}
                        data-testid="social-schedule-date"
                      />
                      <input
                        type="time"
                        value={when.time}
                        onChange={(e) => { setWhen((w) => ({ ...w, time: e.target.value })); setReport(null); }}
                        data-testid="social-schedule-time"
                      />
                      <span className="social-tz" data-testid="social-timezone">
                        <Clock size={12} /> {tz}
                      </span>
                    </div>
                  )}
                </div>

                {report && !report.ok && (
                  <div className="social-alert error" data-testid="social-validation-errors">
                    <AlertTriangle size={15} />
                    <ul>
                      {report.errors.map((issue, i) => <li key={i}>{issue.message}</li>)}
                    </ul>
                  </div>
                )}
                {report?.warnings?.length > 0 && (
                  <div className="social-alert warning" data-testid="social-validation-warnings">
                    <AlertTriangle size={15} />
                    <ul>
                      {report.warnings.map((issue, i) => <li key={i}>{issue.message}</li>)}
                    </ul>
                  </div>
                )}
              </>
            )}
          </section>
        </div>

        <footer className="social-modal-foot">
          {step === 'confirm' && (
            <button className="social-btn ghost" onClick={() => setStep('compose')}
                    data-testid="social-back-btn">
              <ArrowLeft size={14} /> Volver
            </button>
          )}
          <div className="social-foot-right">
            <button className="social-btn ghost" onClick={onClose} data-testid="social-cancel-btn">
              Cerrar
            </button>
            {step === 'compose' && connected && (
              <button className="social-btn primary" onClick={review} disabled={busy}
                      data-testid="social-review-btn">
                {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                Revisar y continuar
              </button>
            )}
            {step === 'confirm' && (
              <button className="social-btn primary" onClick={confirm} disabled={busy}
                      data-testid="social-confirm-btn">
                {busy
                  ? <><Loader2 size={14} className="animate-spin" /> Publicando…</>
                  : mode === 'scheduled'
                    ? <><CalendarClock size={14} /> Confirmar y programar</>
                    : <><Send size={14} /> Confirmar y publicar</>}
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
};

const Summary = ({ channels, caption, perChannel, perOptions, mode, when, tz, warnings }) => (
  <div className="social-summary" data-testid="social-summary">
    <div className="social-summary-row">
      <span>Redes</span>
      <div className="social-summary-channels">
        {channels.map((c) => (
          <span key={c.id} className="social-chip ok">
            <PlatformIcon platform={c.platform} size={11} />
            {c.display_name || platformMeta(c.platform).label}
            {(c.capabilities?.post_types || []).length > 0
              ? ` · ${POST_TYPE_LABEL[perOptions?.[c.id] || 'post']}`
              : ''}
          </span>
        ))}
      </div>
    </div>
    <div className="social-summary-row">
      <span>Cuándo</span>
      <strong data-testid="social-summary-when">
        {mode === 'scheduled'
          ? `${when.date} a las ${when.time} (${tz})`
          : 'Ahora mismo'}
      </strong>
    </div>
    <div className="social-summary-row">
      <span>Texto</span>
      <p className="social-summary-caption">{caption || <em>sin texto general</em>}</p>
    </div>
    {channels.filter((c) => perChannel[c.id]?.trim()).map((c) => (
      <div className="social-summary-row" key={c.id}>
        <span>{platformMeta(c.platform).label}</span>
        <p className="social-summary-caption">{perChannel[c.id]}</p>
      </div>
    ))}
    {warnings.length > 0 && (
      <div className="social-alert warning">
        <AlertTriangle size={15} />
        <ul>{warnings.map((w, i) => <li key={i}>{w.message}</li>)}</ul>
      </div>
    )}
  </div>
);

const ResultList = ({ result, onRestart }) => (
  <div className="social-result" data-testid="social-result">
    <ul>
      {(result?.targets || []).map((target) => (
        <li key={target.id} className={target.status} data-testid={`social-result-${target.id}`}>
          {target.status === 'failed'
            ? <XCircle size={15} className="icon fail" />
            : <CheckCircle2 size={15} className="icon ok" />}
          <div>
            <strong>
              <PlatformIcon platform={target.platform} size={12} /> {target.platform_label}
            </strong>
            <span>{TARGET_STATUS_LABEL[target.status] || target.status}</span>
            {target.error_message && <em>{target.error_message}</em>}
            {target.external_link && (
              <a href={target.external_link} target="_blank" rel="noopener noreferrer">Ver publicación</a>
            )}
          </div>
        </li>
      ))}
    </ul>
    <button className="social-btn ghost" onClick={onRestart} data-testid="social-new-post-btn">
      Preparar otra publicación
    </button>
  </div>
);

export default SocialPublishModal;
